/**
 * Phase 8 operational search (Ctrl/⌘+K). EXACT identifiers only — HopEx user id, request references
 * (TD/TW/ADJ/CD/CW/CV, BE/RC), support thread refs and TXIDs. Never a free-text scan over personal
 * data: phone / national ID go through «کاربران» (keyed exact search, audited there).
 */
import { Badge, cx } from '@hopex/ui';
import { useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api, ApiFailure, errorMessage } from '../api.ts';

interface Hit {
  kind: string;
  id: string;
  ref: string;
  userId: string | null;
  status: string | null;
}
const KIND: Record<string, string> = {
  USER: 'کاربر',
  TOMAN_DEPOSIT: 'واریز تومان',
  TOMAN_WITHDRAWAL: 'برداشت تومان',
  ADJUSTMENT: 'اصلاح موجودی',
  BANK_EVENT: 'شاهد بانکی',
  RECON_CASE: 'پرونده‌ی تطبیق',
  CRYPTO_DEPOSIT: 'واریز رمزارز',
  CRYPTO_WITHDRAWAL: 'برداشت رمزارز',
  CONVERSION: 'سفارش تبدیل',
  SUPPORT_THREAD: 'پرونده‌ی پشتیبانی',
};
const TAB: Record<string, string> = {
  TOMAN_DEPOSIT: 'toman',
  TOMAN_WITHDRAWAL: 'toman',
  ADJUSTMENT: 'money-ops',
  CRYPTO_DEPOSIT: 'crypto',
  CRYPTO_WITHDRAWAL: 'crypto',
  CONVERSION: 'trades',
};

export function AdminSearch() {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const open = (h: Hit) => {
    setHits(null);
    setQ('');
    if (h.kind === 'USER') return void navigate({ to: '/users/$id', params: { id: h.id } });
    if (h.kind === 'SUPPORT_THREAD')
      return void navigate({ to: '/support/$id', params: { id: h.id } });
    if (h.userId && TAB[h.kind])
      return void navigate({
        to: '/users/$id',
        params: { id: h.userId },
        search: { tab: TAB[h.kind] },
      });
    return void navigate({ to: h.kind === 'BANK_EVENT' ? '/toman-deposits' : '/reconciliation' });
  };

  const run = async () => {
    const s = q.trim();
    if (s.length < 4) return;
    if (/^0?9\d{9}$|^\d{10}$/.test(s)) {
      setHits([]);
      setNote(
        'شماره‌ی موبایل و کد ملی فقط از بخش «کاربران» (جستجوی دقیق و ممیزی‌شده) جستجو می‌شوند.',
      );
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const r = await api.get<{ hits: Hit[] }>(`/search?q=${encodeURIComponent(s)}`);
      if (r.hits.length === 1 && r.hits[0]) open(r.hits[0]);
      else {
        setHits(r.hits);
        if (r.hits.length === 0) setNote('موردی با این شناسه پیدا نشد.');
      }
    } catch (e) {
      setHits([]);
      setNote(
        e instanceof ApiFailure && e.code === 'VALIDATION_FAILED'
          ? 'فقط شناسه‌ی دقیق: HX-…، کد پیگیری (مثلاً TD-…)، HX-T-… یا TXID.'
          : errorMessage(e),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative hidden w-full max-w-sm md:block" data-admin-search>
      <form
        role="search"
        className="relative"
        onSubmit={(e) => {
          e.preventDefault();
          void run();
        }}
      >
        <label className="sr-only" htmlFor="hx-admin-search">
          جستجوی عملیاتی
        </label>
        <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-fg-subtle">
          <Search aria-hidden className="size-4" />
        </span>
        <input
          id="hx-admin-search"
          ref={input}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setHits(null);
            setNote(null);
          }}
          onKeyDown={(e) => e.key === 'Escape' && (setHits(null), setNote(null))}
          placeholder="جستجوی دقیق: شناسه‌ی کاربر، کد پیگیری یا شناسه‌ی تراکنش"
          title="میان‌بر: Ctrl+K"
          aria-keyshortcuts="Control+K Meta+K"
          aria-busy={busy || undefined}
          // RTL while empty (Persian placeholder beside the icon); identifiers are LTR once typed.
          dir={q ? 'auto' : 'rtl'}
          className="h-10 w-full rounded-md border border-line bg-surface-1 ps-9 pe-3 text-[13px] text-fg outline-hidden placeholder:text-fg-subtle focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus"
        />
      </form>
      {(hits !== null || note) && (
        <div
          role="listbox"
          aria-label="نتایج جستجو"
          className="absolute inset-x-0 top-11 z-50 rounded-md border border-line-strong bg-surface-2 p-1.5 shadow-float"
          data-admin-search-results
        >
          {note && <p className="m-0 px-2 py-1.5 text-[12.5px] text-fg-muted">{note}</p>}
          {hits?.map((h) => (
            <button
              key={`${h.kind}-${h.id}`}
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => open(h)}
              className={cx(
                'flex w-full cursor-pointer items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-start text-[13px] outline-hidden hover:bg-surface-3 focus-visible:outline-2 focus-visible:outline-focus',
              )}
            >
              <span>{KIND[h.kind] ?? h.kind}</span>
              <span className="flex items-center gap-2">
                {h.status && <Badge>{h.status}</Badge>}
                <bdi dir="ltr" className="font-mono text-[12px]">
                  {h.ref}
                </bdi>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
